import type { Engine } from '../types';
import { buildUrl } from '../url';

const placeParam = {
  id: 'place',
  type: 'text' as const,
  prefix: '@',
  pattern: /^[a-z0-9-]{2,40}$/,
  label: 'Location',
  group: 'Location',
  placeholder: 'tacoma-wa',
  help: 'City or zip; dashes become spaces (@tacoma-wa). Defaults to your zip from Settings.',
};

const place = (value: unknown, zip?: string) =>
  typeof value === 'string' ? value.replace(/-/g, ' ') : zip;

export const linkedin: Engine = {
  id: 'linkedin',
  name: 'LinkedIn Jobs',
  bang: 'li',
  aliases: ['linkedin'],
  homepage: 'https://www.linkedin.com/jobs',
  category: 'Jobs',
  description: 'Job listings by date posted, workplace, level and pay.',
  signIn:
    'Signed out, only date posted, Easy Apply and few applicants apply; the rest need you signed in.',
  params: [
    placeParam,
    {
      id: 'posted',
      type: 'choice',
      label: 'Posted',
      group: 'Filters',
      options: [
        { value: 'r86400', token: 'd', label: 'Past 24 hours' },
        { value: 'r604800', token: 'w', label: 'Past week' },
        { value: 'r2592000', token: 'm', label: 'Past month' },
      ],
    },
    {
      id: 'workplace',
      type: 'choice',
      label: 'Workplace',
      group: 'Filters',
      options: [
        { value: '2', token: 'remote', label: 'Remote' },
        { value: '3', token: 'hybrid', label: 'Hybrid' },
        { value: '1', token: 'onsite', label: 'On-site' },
      ],
    },
    {
      id: 'level',
      type: 'choice',
      label: 'Experience',
      group: 'Filters',
      options: [
        { value: '1', token: 'intern', label: 'Internship' },
        { value: '2', token: 'entry', label: 'Entry level' },
        { value: '3', token: 'assoc', label: 'Associate' },
        { value: '4', token: 'senior', label: 'Mid-senior' },
        { value: '5', token: 'director', label: 'Director' },
        { value: '6', token: 'exec', label: 'Executive' },
      ],
    },
    {
      id: 'jobType',
      type: 'choice',
      label: 'Job type',
      group: 'Filters',
      options: [
        { value: 'F', token: 'full', label: 'Full-time' },
        { value: 'P', token: 'part', label: 'Part-time' },
        { value: 'C', token: 'contract', label: 'Contract' },
        { value: 'T', token: 'temp', label: 'Temporary' },
      ],
    },
    {
      id: 'salary',
      type: 'choice',
      label: 'Salary at least',
      group: 'Filters',
      options: [40, 60, 80, 100, 120, 140, 160, 180, 200].map((k, i) => ({
        value: String(i + 1),
        token: `${k}k`,
        label: `$${k}k+`,
      })),
    },
    {
      id: 'easy',
      type: 'flag',
      token: 'easy',
      label: 'Easy Apply',
      group: 'Options',
    },
    {
      id: 'few',
      type: 'flag',
      token: 'few',
      label: 'Under 10 applicants',
      group: 'Options',
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [{ value: 'DD', token: 'newest', label: 'Most recent' }],
    },
  ],
  build({ query, values, settings }) {
    return buildUrl('https://www.linkedin.com/jobs/search/', {
      keywords: query,
      location: place(values.place, settings.zip),
      f_TPR: values.posted as string | undefined,
      f_WT: values.workplace as string | undefined,
      f_E: values.level as string | undefined,
      f_JT: values.jobType as string | undefined,
      f_SB2: values.salary as string | undefined,
      f_AL: values.easy === true && 'true',
      f_EA: values.few === true && 'true',
      sortBy: values.sort as string | undefined,
    });
  },
};

export const indeed: Engine = {
  id: 'indeed',
  name: 'Indeed',
  bang: 'indeed',
  aliases: ['ind'],
  homepage: 'https://www.indeed.com',
  category: 'Jobs',
  experimental: true,
  description: 'Job listings by date, distance, remote and job type.',
  params: [
    placeParam,
    {
      id: 'distance',
      when: { param: 'workplace', in: ['attr(PAXZC)'], orUnset: true },
      whenLabel: 'doesn’t apply to remote jobs',
      type: 'number',
      suffix: 'mi',
      label: 'Within (miles)',
      group: 'Location',
      placeholder: '25',
    },
    {
      id: 'posted',
      type: 'choice',
      label: 'Posted',
      group: 'Filters',
      options: [
        { value: '1', token: 'd', label: 'Last 24 hours' },
        { value: '3', token: '3d', label: 'Last 3 days' },
        { value: '7', token: 'w', label: 'Last 7 days' },
        { value: '14', token: '2w', label: 'Last 14 days' },
      ],
    },
    {
      id: 'workplace',
      type: 'choice',
      label: 'Workplace',
      group: 'Filters',
      options: [
        { value: 'attr(DSQF7)', token: 'remote', label: 'Remote' },
        { value: 'attr(PAXZC)', token: 'hybrid', label: 'Hybrid' },
      ],
    },
    {
      id: 'jobType',
      type: 'choice',
      label: 'Job type',
      group: 'Filters',
      options: [
        { value: 'attr(CF3CP)', token: 'full', label: 'Full-time' },
        { value: 'attr(75GKK)', token: 'part', label: 'Part-time' },
        { value: 'attr(NJXCK)', token: 'contract', label: 'Contract' },
        { value: 'attr(4HKF7)', token: 'temp', label: 'Temporary' },
        { value: 'attr(VDTG7)', token: 'intern', label: 'Internship' },
      ],
    },
    {
      id: 'level',
      type: 'choice',
      label: 'Experience',
      group: 'Filters',
      options: [
        { value: 'explvl(ENTRY_LEVEL)', token: 'entry', label: 'Entry level' },
        { value: 'explvl(MID_LEVEL)', token: 'mid', label: 'Mid level' },
        {
          value: 'explvl(SENIOR_LEVEL)',
          token: 'senior',
          label: 'Senior level',
        },
      ],
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [{ value: 'date', token: 'newest', label: 'Newest' }],
    },
  ],
  build({ query, values, settings }) {
    const codes = [values.workplace, values.jobType, values.level].filter(
      (v): v is string => typeof v === 'string'
    );
    return buildUrl('https://www.indeed.com/jobs', {
      q: query,
      l: place(values.place, settings.zip),
      radius: values.distance as number | undefined,
      fromage: values.posted as string | undefined,
      sc: codes.length ? `0kf:${codes.join('')};` : undefined,
      sort: values.sort as string | undefined,
    });
  },
};
